import merge from 'lodash/merge';
import type { Location } from 'slate';
import { Editor, Node, Path, Range, Transforms } from 'slate';
import type { DeepPartial } from 'ts-essentials';

import { degreesToStAngle, pxToEmu, scaleDimensionsToFit } from '../../util';
import {
  AlineTransforms,
  createDefaultDrawing,
  createInlineDrawingProps,
  EditingMode,
  isDrawingElement,
  Queries,
} from '../internal';
import type {
  DrawingBlip,
  DrawingBlipFill,
  DrawingEmbed,
  DrawingFileMap,
  DrawingProps,
} from '../internal';

const DrawingTransforms = {
  insertDrawing(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: { drawingImgId: string; imgHeightPx: number; imgWidthPx: number };
      at?: Location;
    }
  ) {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        authorId,
        editingMode,
        props: { drawingImgId, imgHeightPx, imgWidthPx },
      } = options;
      if (!at) return;

      const atRange = Editor.range(editor, at);
      const [start, end] = Editor.edges(editor, atRange);
      const startRef = Editor.pointRef(editor, start);
      const endRef = Editor.pointRef(editor, end);

      if (Range.isExpanded(atRange)) {
        AlineTransforms.deleteContent(editor, {
          at,
          authorId,
          direction: 'backward',
          editingMode,
          voids: true,
        });
      }

      const startPointAfterDelete = startRef.unref();
      const endPointAfterDelete = endRef.unref();
      // Backfill with endPointAfterDelete since the start gets nulled out by Slate during non-tracking-changes deletion.
      const pointAfterDelete = startPointAfterDelete ?? endPointAfterDelete;
      if (!pointAfterDelete) return;

      const pageHeightPx = Queries.activePageHeight(editor, 'px');
      const containerWidthPx = Queries.getContainerContentWidthPx(editor, {
        // TODO: No need to use Path.parent once getRMPathsFromCMPath supports text nodes
        at: Path.parent(pointAfterDelete.path),
      });

      const { scaledHeight, scaledWidth } =
        imgWidthPx > containerWidthPx || imgHeightPx > pageHeightPx
          ? scaleDimensionsToFit({
              maxHeight: pageHeightPx,
              maxWidth: containerWidthPx,
              originalHeight: imgHeightPx,
              originalWidth: imgWidthPx,
            })
          : { scaledHeight: imgHeightPx, scaledWidth: imgWidthPx };

      const drawingProps = createInlineDrawingProps({
        cx: Math.round(pxToEmu(scaledWidth)).toString(),
        cy: Math.round(pxToEmu(scaledHeight)).toString(),
        embed: drawingImgId,
        id: drawingImgId,
      });

      const drawing = createDefaultDrawing({
        drawingImgId,
        drawingProps,
        suggestedEdits:
          editingMode === EditingMode.SUGGESTING
            ? [
                {
                  authorId,
                  type: 'insertDrawing',
                  value: {},
                },
              ]
            : [],
      });

      Transforms.insertNodes(editor, drawing, {
        at: pointAfterDelete,
        select: true,
        voids: true,
      });
    });
  },
  setDrawingDimensions(
    editor: Editor,
    options: {
      at: Path;
      props: {
        height: number;
        rotation: number;
        width: number;
      };
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at,
        props: { height, rotation, width },
      } = options;
      const cx = Math.round(pxToEmu(width)).toString();
      const cy = Math.round(pxToEmu(height)).toString();
      const rot = degreesToStAngle(rotation).toString();

      AlineTransforms.setDrawingProps(editor, {
        at,
        editingMode: EditingMode.EDITING,
        props: {
          inline: {
            extent: {
              cx,
              cy,
            },
            graphic: { graphicData: { pic: { spPr: { xfrm: { ext: { cx, cy }, rot } } } } },
          },
        },
      });
    });
  },
  setDrawingProps(
    editor: Editor,
    options: {
      at: Path;
      editingMode: EditingMode;
      props: DeepPartial<DrawingProps>;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at, props } = options;
      const drawing = Node.get(editor, at);
      if (!isDrawingElement(drawing)) return;

      const { children, ...drawingProps } = drawing;
      const mergedProps: DrawingProps = merge({}, drawingProps, props);

      Transforms.setNodes(editor, mergedProps, { at, match: isDrawingElement, voids: true });
    });
  },
  updateDrawingFileIds(
    editor: Editor,
    options: {
      drawingMap: DrawingFileMap;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { drawingMap } = options;
      const drawingEntries = Queries.getDrawingEntries(editor, { at: [] });
      drawingEntries.forEach(([drawing, drawingPath]) => {
        const newImageId = drawingMap[drawing.drawingImgId];
        if (!newImageId) throw new Error(`Expected drawing ${drawing.drawingImgId} in Map.`);

        const blipFill: DrawingBlipFill | undefined =
          drawing.inline?.graphic?.graphicData?.pic?.blipFill;
        const embedKey: keyof DrawingEmbed = 'embed';
        const blip: DrawingBlip =
          blipFill?.blip && embedKey in blipFill.blip
            ? {
                ...blipFill?.blip,
                embed: newImageId,
              }
            : {
                ...blipFill?.blip,
                link: newImageId,
              };

        AlineTransforms.setDrawingProps(editor, {
          at: drawingPath,
          editingMode: EditingMode.EDITING,
          props: {
            drawingImgId: newImageId,
            inline: {
              docPr: { id: newImageId },
              graphic: { graphicData: { pic: { blipFill: { blip } } } },
            },
          },
        });
      });
    });
  },
};

export default DrawingTransforms;
